import type { MouseEvent, ReactNode } from 'react'

import { Button, message, Modal, Space, Typography } from 'antd'

import {
  EApiErrorCode,
  getErrorMessageByCode
} from '@web/shared/enums/errors/error-code.enum'

type ApiErrorData = {
  errorCode?: EApiErrorCode
  code?: EApiErrorCode
  message?: string
  error?: string
}

type ApiError = {
  status?: number | string
  data?: ApiErrorData
}

type ApiErrorInfo = {
  status?: number | string
  title: string
  message: string
  details: string
}

const HTTP_ERROR_MESSAGES: Record<number, string> = {
  400: 'Проверьте корректность отправленных данных.',
  403: 'Недостаточно прав для выполнения операции.',
  404: 'Запрашиваемые данные не найдены.',
  409: 'Операция не может быть выполнена из-за конфликта данных.',
  500: 'Произошла внутренняя ошибка сервера. Попробуйте позже.'
}

const HTTP_ERROR_DETAILS: Record<number, string> = {
  400: 'Сервер отклонил запрос из-за некорректных данных.',
  403: 'Сервер запретил выполнение операции для текущего пользователя.',
  404: 'Сервер не нашел запрашиваемый ресурс.',
  409: 'Сервер обнаружил конфликт данных при выполнении операции.',
  500: 'Сервер не смог обработать запрос из-за внутренней ошибки. Повторите действие позже.'
}

const FETCH_ERROR_MESSAGES: Record<string, string> = {
  FETCH_ERROR: 'Не удалось выполнить запрос к серверу.',
  PARSING_ERROR: 'Не удалось обработать ответ сервера.',
  TIMEOUT_ERROR: 'Превышено время ожидания ответа сервера.',
  CUSTOM_ERROR: 'Ошибка при выполнении запроса.'
}

const JSON_TOKEN_REGEXP =
  /("(?:\\u[\da-fA-F]{4}|\\[^u]|[^\\"])*"(\s*:)?|\btrue\b|\bfalse\b|\bnull\b|-?\d+(?:\.\d*)?(?:[eE][+-]?\d+)?)/g

const renderJsonToken = (token: string, key: number): ReactNode => {
  let color = '#1565c0'

  if (token.startsWith('"')) {
    color = token.endsWith(':') ? '#7b1fa2' : '#2e7d32'
  } else if (token === 'true' || token === 'false') {
    color = '#d84315'
  } else if (token === 'null') {
    color = '#6d4c41'
  }

  return (
    <span key={key} style={{ color }}>
      {token}
    </span>
  )
}

const renderHighlightedJson = (json: string): ReactNode[] => {
  const nodes: ReactNode[] = []
  let lastIndex = 0

  json.replace(JSON_TOKEN_REGEXP, (match, ...args: unknown[]) => {
    const index = args.at(-2) as number

    if (index > lastIndex) nodes.push(json.slice(lastIndex, index))

    nodes.push(renderJsonToken(match, index))
    lastIndex = index + match.length

    return match
  })

  if (lastIndex < json.length) nodes.push(json.slice(lastIndex))

  return nodes
}

const isApiError = (error: unknown): error is ApiError =>
  typeof error === 'object' && error !== null && 'status' in error

const getApiErrorInfo = (error?: unknown): ApiErrorInfo => {
  if (!isApiError(error)) {
    return {
      title: 'Ошибка',
      message: 'Ошибка при выполнении запроса.',
      details: 'Не удалось получить подробную информацию об ошибке.'
    }
  }

  const status = error.status
  const data = error.data
  const errorCode = data?.errorCode ?? data?.code
  const mappedMessage = getErrorMessageByCode(errorCode)

  if (typeof status === 'number') {
    const messageText =
      mappedMessage ??
      HTTP_ERROR_MESSAGES[status] ??
      'Ошибка при выполнении запроса.'

    return {
      status,
      title: `Ошибка ${status}`,
      message: `Код ${status} | ${messageText}`,
      details:
        HTTP_ERROR_DETAILS[status] ??
        'Сервер вернул ошибку при выполнении запроса.'
    }
  }

  const statusText = status ?? 'UNKNOWN_ERROR'
  const messageText =
    FETCH_ERROR_MESSAGES[statusText] ?? 'Ошибка при выполнении запроса.'

  return {
    status: statusText,
    title: 'Ошибка запроса',
    message: `${statusText} | ${messageText}`,
    details: 'Запрос завершился ошибкой до получения корректного HTTP-ответа.'
  }
}

export const showApiError = (error?: unknown): void => {
  const errorInfo = getApiErrorInfo(error)
  const errorJson = error ? JSON.stringify(error, null, 2) : ''

  const copyText = (text: string): void => {
    navigator.clipboard
      .writeText(text)
      .then(() => message.success('Текст скопирован'))
      .catch(() => message.error('Не удалось скопировать текст'))
  }

  const handleDetailsClick = (event: MouseEvent<HTMLElement>): void => {
    event.preventDefault()

    Modal.confirm({
      title: errorInfo.title,
      icon: null,
      width: 720,
      cancelText: 'Закрыть',
      content: (
        <>
          <Typography.Paragraph>{errorInfo.details}</Typography.Paragraph>
          {error ? (
            <Typography.Paragraph>
              <pre>{renderHighlightedJson(errorJson)}</pre>
            </Typography.Paragraph>
          ) : null}
        </>
      ),
      footer: (_, { CancelBtn }) => (
        <Space>
          <Button disabled={!errorJson} onClick={() => copyText(errorJson)}>
            Копировать ошибку
          </Button>
          <CancelBtn />
        </Space>
      )
    })
  }

  message.error(
    <span>
      {errorInfo.message}{' '}
      <Typography.Link onClick={handleDetailsClick}>Подробнее</Typography.Link>
    </span>
  )
}

export const showInternalServerError = showApiError
